import React, { useEffect, useState } from "react";
import {
  GiCrescentBlade,
  GiMoonOrbit,
  GiSun,
  GiNightSky,
  GiCrystalBall,
  GiHamburgerMenu,
} from "react-icons/gi";
import { ChevronDown, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useTheme } from "../theme/ThemeContext";
import { useAuth } from "../auth/AuthContext";
import { avatarClient } from "../features/auth/api/avatarClient";

const navItemCls =
  "text-[12px] font-accent uppercase tracking-[0.22em] text-ink-soft hover:text-ink transition-colors";

const HeaderAvatar = ({ src, label }) => {
  if (src) {
    return <img src={src} alt={label} width="28" height="28" data-ui="header-profile-avatar" />;
  }
  return <GiCrescentBlade className="text-base" />;
};

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, user, logout } = useAuth();
  const isDark = theme === "dark";

  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [desktopMenuSuppressed, setDesktopMenuSuppressed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadAvatar = async () => {
      if (!isAuthenticated) {
        setAvatarUrl("");
        return;
      }
      try {
        const response = await avatarClient.get();
        if (mounted) setAvatarUrl(response?.avatar?.url || "");
      } catch (_) {
        if (mounted) setAvatarUrl("");
      }
    };
    loadAvatar();
    const refresh = () => loadAvatar();
    window.addEventListener("focus", refresh);
    window.addEventListener("hashchange", refresh);
    return () => {
      mounted = false;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("hashchange", refresh);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const close = () => {
      setMobileOpen(false);
      setDesktopMenuSuppressed(true);
    };
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const themeAria = isDark
    ? lang === "pl" ? "Przełącz na tryb dzienny" : "Switch to light mode"
    : lang === "pl" ? "Przełącz na tryb nocny" : "Switch to dark mode";

  const menuAria = lang === "pl" ? "Otwórz menu" : "Open menu";
  const closeAria = (t.nav && t.nav.close) || (lang === "pl" ? "Zamknij" : "Close");
  const profileLabel = (t.nav && t.nav.profile) || (lang === "pl" ? "Profil" : "Profile");
  const signOutLabel = (t.nav && t.nav.signOut) || (lang === "pl" ? "Wyloguj" : "Sign out");
  const dreamTraceLabel = lang === "pl" ? "Ślad snów" : "Dream trace";
  const storeLabel = lang === "pl" ? "Sklep" : "Store";

  const handleMobileLogout = async () => {
    await logout?.();
    setMobileOpen(false);
    if (typeof window !== "undefined") window.location.hash = "#top";
  };

  const handleDropdownLinkClick = (e, href) => {
    setDesktopMenuSuppressed(true);
    if (!href.startsWith("#") || typeof window === "undefined") return;
    e.preventDefault();
    window.location.hash = href;
  };

  const navPrimaryLinks = [
    { href: "#dreams", label: (t.nav && t.nav.dreams) || (lang === "pl" ? "Sny" : "Dreams"), test: "nav-dreams" },
    { href: "#/szybki-zapis", label: lang === "pl" ? "Szybki zapis" : "Quick note", test: "nav-quick-save" },
    { href: "#/pokoik", label: (t.nav && t.nav.pokoik) || (lang === "pl" ? "Pokoik" : "Room"), test: "nav-pokoik" },
    {
      href: "#/symbolika",
      label: (t.nav && t.nav.symbols) || (lang === "pl" ? "Symbolika" : "Symbols"),
      test: "nav-symbols",
      icon: <GiCrystalBall className="text-sm" />,
    },
    { href: "#/sklep", label: storeLabel, test: "nav-store" },
  ];
  const navSecondaryLinks = [
    { href: "#how", label: t.nav.howItWorks, test: "nav-how" },
    { href: "#/strefa", label: dreamTraceLabel, test: "nav-strefa" },
    { href: "#/blog", label: "Blog", test: "nav-blog" },
    { href: "#faq", label: t.nav.faq, test: "nav-faq" },
  ];
  const navLinks = [...navPrimaryLinks, ...navSecondaryLinks];
  const navGroupLabel = (t.nav && t.nav.menu) || "Menu";
  const navGroupPanelCls = isDark
    ? "border-gold-muted/15 bg-ink-deep/88 shadow-[0_14px_30px_rgba(0,0,0,0.28)]"
    : "border-ink/10 bg-parchment/82 shadow-[0_12px_24px_rgba(43,35,27,0.08)]";

  return (
    <>
      <header
        className="sticky top-0 z-40 backdrop-blur-md bg-parchment/85 border-b border-ink/10"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-20 flex items-center justify-between gap-4">
          <a
            href="#top"
            className="flex items-center gap-3 group flex-shrink-0"
            data-testid="brand-logo"
          >
            <GiMoonOrbit className="text-2xl text-ink group-hover:text-gold-muted transition-colors" />
            <div className="leading-none">
              <div className="font-accent text-base text-ink tracking-[0.3em]">
                MORFEUSZ
              </div>
              <div className="font-heading italic text-sm text-ink-soft -mt-0.5">
                dreams
              </div>
            </div>
          </a>

          <nav className="hidden md:flex items-center gap-6">
            {navPrimaryLinks.map((link) => (
              <a
                key={link.test}
                href={link.href}
                className={navItemCls}
                data-testid={link.test}
              >
                {link.icon ? (
                  <span className="inline-flex items-center gap-1.5">
                    {link.icon}
                    {link.label}
                  </span>
                ) : (
                  link.label
                )}
              </a>
            ))}

            <div
              className="relative group"
              onMouseLeave={() => setDesktopMenuSuppressed(false)}
              onFocus={() => setDesktopMenuSuppressed(false)}
            >
              <a
                href="#top"
                onClick={(e) => e.preventDefault()}
                onMouseEnter={() => setDesktopMenuSuppressed(false)}
                className={`${navItemCls} inline-flex cursor-pointer items-center gap-1`}
                aria-haspopup="true"
                data-testid="nav-group-menu"
              >
                {navGroupLabel}
                <ChevronDown size={12} className="opacity-55" aria-hidden="true" />
              </a>
              <div className={`${desktopMenuSuppressed ? "hidden" : "invisible absolute left-1/2 top-full z-50 w-max -translate-x-1/2 pt-2 text-center opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"}`}>
                <div className={`flex flex-col gap-1.5 border px-3.5 py-2 backdrop-blur-md ${navGroupPanelCls}`}>
                  {navSecondaryLinks.map((link) => (
                    <a
                      key={link.test}
                      href={link.href}
                      className={`${navItemCls} whitespace-nowrap px-1 py-0.5`}
                      onClick={(e) => handleDropdownLinkClick(e, link.href)}
                      data-testid={link.test}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="flex items-center gap-2 pl-4 ml-2 border-l border-ink/15"
              data-testid="language-switcher"
            >
              <button
                onClick={() => setLang("pl")}
                data-testid="lang-switcher-pl"
                className={`font-accent text-[11px] uppercase tracking-[0.25em] transition-colors ${
                  lang === "pl" ? "text-gold-muted" : "text-ink/40 hover:text-ink"
                }`}
              >
                PL
              </button>
              <span className="text-ink/30">·</span>
              <button
                onClick={() => setLang("en")}
                data-testid="lang-switcher-en"
                className={`font-accent text-[11px] uppercase tracking-[0.25em] transition-colors ${
                  lang === "en" ? "text-gold-muted" : "text-ink/40 hover:text-ink"
                }`}
              >
                EN
              </button>
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="mdThemeToggle"
              aria-label={themeAria}
              title={themeAria}
              data-testid="theme-toggle"
              data-theme-state={theme}
            >
              {isDark ? <GiSun className="text-base" /> : <GiNightSky className="text-base" />}
            </button>

            {isAuthenticated ? (
              <a
                href="#/profil"
                className="inline-flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.22em] text-ink hover:text-gold-muted transition-colors"
                data-testid="profile-link"
                title={user?.email || ""}
              >
                <HeaderAvatar src={avatarUrl} label={profileLabel} />
                {profileLabel}
              </a>
            ) : (
              <a
                href="#/zaloguj"
                className="inline-flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.22em] text-ink hover:text-gold-muted transition-colors"
                data-testid="signin-btn"
              >
                <GiCrescentBlade className="text-base" />
                {t.nav.signIn}
              </a>
            )}
          </nav>

          <div className="flex md:hidden items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="mdThemeToggle"
              aria-label={themeAria}
              title={themeAria}
              data-testid="theme-toggle-mobile"
              data-theme-state={theme}
            >
              {isDark ? <GiSun className="text-base" /> : <GiNightSky className="text-base" />}
            </button>
            {isAuthenticated && (
              <a
                href="#/profil"
                className="mdThemeToggle"
                aria-label={profileLabel}
                title={profileLabel}
                data-testid="profile-link-mobile-top"
              >
                <HeaderAvatar src={avatarUrl} label={profileLabel} />
              </a>
            )}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="mdThemeToggle"
              aria-label={menuAria}
              aria-expanded={mobileOpen}
              aria-controls="mobile-drawer"
              data-testid="menu-toggle"
            >
              <GiHamburgerMenu className="text-lg" />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div
          className="mdDrawer__backdrop"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-drawer"
          id="mobile-drawer"
          onClick={() => setMobileOpen(false)}
        >
          <aside
            className="mdDrawer"
            data-theme-variant={theme}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mdDrawer__head">
              <a
                href="#top"
                className="flex items-center gap-2"
                onClick={() => setMobileOpen(false)}
              >
                <GiMoonOrbit className="text-xl" />
                <div className="leading-none">
                  <div className="font-accent text-sm tracking-[0.3em]">MORFEUSZ</div>
                  <div className="font-heading italic text-xs opacity-70 -mt-0.5">dreams</div>
                </div>
              </a>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="mdThemeToggle"
                aria-label={closeAria}
                data-testid="menu-close"
              >
                <X size={18} />
              </button>
            </div>

            <nav className="mdDrawer__nav" data-testid="mobile-nav">
              {navLinks.map((link) => (
                <a
                  key={link.test + "-m"}
                  href={link.href}
                  className="mdDrawer__link"
                  onClick={() => setMobileOpen(false)}
                  data-testid={link.test + "-mobile"}
                >
                  {link.icon ? (
                    <span className="inline-flex items-center gap-2">
                      {link.icon}
                      {link.label}
                    </span>
                  ) : (
                    link.label
                  )}
                </a>
              ))}

              {isAuthenticated ? (
                <>
                  <a
                    href="#/profil"
                    className="mdDrawer__link mdDrawer__link--accent"
                    onClick={() => setMobileOpen(false)}
                    data-testid="profile-link-mobile"
                  >
                    <span className="inline-flex items-center gap-2">
                      <HeaderAvatar src={avatarUrl} label={profileLabel} />
                      {profileLabel}
                    </span>
                  </a>
                  <div data-ui="mobile-drawer-signout-wrap">
                    <button
                      type="button"
                      className="mdDrawer__link mdDrawer__link--accent"
                      onClick={handleMobileLogout}
                      data-testid="signout-btn-mobile"
                      data-ui="mobile-drawer-signout"
                    >
                      {signOutLabel}
                    </button>
                  </div>
                </>
              ) : (
                <a
                  href="#/zaloguj"
                  className="mdDrawer__link mdDrawer__link--accent"
                  onClick={() => setMobileOpen(false)}
                  data-testid="signin-btn-mobile"
                >
                  <span className="inline-flex items-center gap-2">
                    <GiCrescentBlade className="text-base" />
                    {t.nav.signIn}
                  </span>
                </a>
              )}
            </nav>

            <div className="mdDrawer__foot">
              <div
                className="mdDrawer__lang"
                data-testid="language-switcher-mobile"
              >
                <button
                  onClick={() => setLang("pl")}
                  data-testid="lang-switcher-pl-mobile"
                  className={`mdDrawer__langBtn ${lang === "pl" ? "is-active" : ""}`}
                >
                  PL
                </button>
                <span className="opacity-40">·</span>
                <button
                  onClick={() => setLang("en")}
                  data-testid="lang-switcher-en-mobile"
                  className={`mdDrawer__langBtn ${lang === "en" ? "is-active" : ""}`}
                >
                  EN
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};
